if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        /* 28-10: met de rondloop-kopieën (mb-custom.js) klopt Dawn's paginanummer niet meer; neem dan de foto in het midden */
        const list = this.elements.viewer.slider;
        let current = event.detail.currentElement;
        if (list && list.hasAttribute('data-mb-loop')) current = list.querySelector(':scope > li.mb-cur[data-media-id]') || current;
        if (!current || !current.dataset) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${current.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          /* MB: variantwissel. Zelfde volgorde als de server (variantfoto eerst, rest op productpositie),
             zodat de serverupdate daarna niets meer hoeft te verschuiven; slider direct naar foto 1,
             zonder heen-en-weer schuiven en zonder dat de pagina omhoog springt (zoals willamelbourne). */
          /* MB 2026-10-22: Safari/iPhone houdt bij het herschikken de foto vast die op dat moment in beeld
             was (scroll-snap "re-snap"). Schoof die foto naar zijn productplek (bv. 18), dan sprong de slider
             mee naar foto 18. Daarom: vastklikken uit tijdens het herschikken, direct naar foto 1 (scrollLeft,
             werkt ook op oudere iPhones waar behavior 'instant' niet bestaat) en een kort vangnet. */
          const mbList = activeMedia.parentElement;
          this.mbFreeze(mbList);
          this.mbOrderForVariant(mbList, activeMedia, '[data-media-id]');

          let mbThumbList = null;
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              mbThumbList = activeThumbnail.parentElement;
              this.mbFreeze(mbThumbList);
              this.mbOrderForVariant(mbThumbList, activeThumbnail, '[data-target]');
            }
          }

          mbList.scrollLeft = mbList.mbLoopHome ? mbList.mbLoopHome() : 0;
          if (mbThumbList) mbThumbList.scrollLeft = 0;
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          this.mbUnfreeze(mbList, true);
          if (mbThumbList) this.mbUnfreeze(mbThumbList, false);
          this.playActiveMedia(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            this.setActiveThumbnail(activeThumbnail);
          }
          return;
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      mbFreeze(list) {
        list.mbFreezeToken = (list.mbFreezeToken || 0) + 1;
        list.style.scrollSnapType = 'none';
        list.style.scrollBehavior = 'auto';
      }

      mbUnfreeze(list, guard) {
        const token = list.mbFreezeToken;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            if (list.mbFreezeToken !== token) return;
            list.scrollLeft = list.mbLoopHome ? list.mbLoopHome() : 0;
            list.style.scrollSnapType = '';
            list.style.scrollBehavior = '';
            if (!guard) return;
            /* vangnet: springt de slider in de eerste 0,8 s zonder aanraking toch weg, dan terug naar foto 1 */
            let touched = false;
            const onTouch = () => (touched = true);
            const onScroll = () => {
              const home = list.mbLoopHome ? list.mbLoopHome() : 0;
              if (touched || list.mbFreezeToken !== token || Math.abs(list.scrollLeft - home) <= 2) return;
              list.style.scrollSnapType = 'none';
              list.scrollLeft = home;
              requestAnimationFrame(() => {
                if (list.mbFreezeToken === token) list.style.scrollSnapType = '';
              });
            };
            list.addEventListener('touchstart', onTouch, { passive: true });
            list.addEventListener('pointerdown', onTouch, { passive: true });
            list.addEventListener('scroll', onScroll, { passive: true });
            setTimeout(() => {
              list.removeEventListener('touchstart', onTouch);
              list.removeEventListener('pointerdown', onTouch);
              list.removeEventListener('scroll', onScroll);
            }, 800);
          })
        );
      }

      mbOrderForVariant(list, active, selector) {
        /* 28-10: de rondloop-kopieën (li.mb-loop, mb-custom.js) tellen niet mee; ze staan vooraan en achteraan */
        const items = Array.from(list.children).filter((item) => !item.classList.contains('mb-loop'));
        const lead = list.firstElementChild && list.firstElementChild.classList.contains('mb-loop') ? 1 : 0;
        if (!items.every((item) => item.matches(selector) && item.dataset.mbPos)) {
          if (items[0] !== active) list.insertBefore(active, list.children[lead]);
          return;
        }
        const ordered = items
          .filter((item) => item !== active)
          .sort((a, b) => Number(a.dataset.mbPos) - Number(b.dataset.mbPos));
        ordered.unshift(active);
        // alleen verplaatsen wat niet op zijn plek staat
        ordered.forEach((item, index) => {
          const current = list.children[index + lead];
          if (current !== item) list.insertBefore(item, current);
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
